import { useQuery } from '@tanstack/react-query';

import type { FinancialStatementResponse } from '@dcc-erp/shared';

import { api } from '../api';

// All three statements take similar filters; the per-statement helpers below
// just hit different routes. Period is YYYYMM (e.g. '202605'); ISO date params
// override period when both are passed (matches the service-side precedence).

// Line-level dimension filters (Phase 42-A4), shared by all three statements.
// UUIDs; produce a single-branch / single-cost-center statement.
export interface DimensionFilters {
  branchId?: string;
  departmentId?: string;
}

export interface BalanceSheetFilters extends DimensionFilters {
  period?: string;
  asOfDate?: string; // YYYY-MM-DD
  compareAsOfDate?: string;
}

export interface RangeStatementFilters extends DimensionFilters {
  period?: string;
  dateFrom?: string;
  dateTo?: string;
  compareFrom?: string;
  compareTo?: string;
}

function buildQs(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.set(k, v);
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export function useBalanceSheet(
  companyId: string | undefined,
  filters: BalanceSheetFilters,
  enabled = true,
) {
  const search = buildQs(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: ['financial-statements', 'balance-sheet', companyId, filters],
    queryFn: () =>
      api<FinancialStatementResponse>(
        `/companies/${companyId}/financial-statements/balance-sheet${search}`,
      ),
    enabled: !!companyId && enabled,
  });
}

export function useIncomeStatement(
  companyId: string | undefined,
  filters: RangeStatementFilters,
  enabled = true,
) {
  const search = buildQs(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: ['financial-statements', 'income-statement', companyId, filters],
    queryFn: () =>
      api<FinancialStatementResponse>(
        `/companies/${companyId}/financial-statements/income-statement${search}`,
      ),
    enabled: !!companyId && enabled,
  });
}

export function useCashFlow(
  companyId: string | undefined,
  filters: RangeStatementFilters,
  enabled = true,
) {
  const search = buildQs(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: ['financial-statements', 'cash-flow', companyId, filters],
    queryFn: () =>
      api<FinancialStatementResponse>(
        `/companies/${companyId}/financial-statements/cash-flow${search}`,
      ),
    enabled: !!companyId && enabled,
  });
}
